// Libretas de direcciones que trae cada persona al pasarse al CRM: vCard (.vcf, lo que exporta OX App Suite) y CSV
// (lo que exporta Outlook, en ingles o en español; separado por comas o punto y coma).
import { normalize } from './format';

export type ContactoLibreta = { nombre: string; apellido: string; email: string; telefono: string; empresa: string; cargo: string };

/* ---------------- vCard ---------------- */

const qp = (s: string) =>
  decodeURIComponent(
    s
      .replace(/=\r?\n/g, '')
      .replace(/%/g, '%25')
      .replace(/=([0-9A-F]{2})/gi, '%$1'),
  );

const valorVcard = (linea: string) => {
  const i = linea.indexOf(':');
  const params = linea.slice(0, i).toUpperCase();
  const v = linea.slice(i + 1);
  const texto = params.includes('QUOTED-PRINTABLE') ? qp(v) : v;
  return texto.replace(/\\n/gi, ' ').replace(/\\([,;\\])/g, '$1').trim();
};

export function leerVcard(texto: string): ContactoLibreta[] {
  const lineas = texto.replace(/\r\n/g, '\n').replace(/\n[ \t]/g, '').split('\n');
  const out: ContactoLibreta[] = [];
  let c: (ContactoLibreta & { fn: string; telPrio: number }) | null = null;
  for (const l of lineas) {
    const clave = l.slice(0, Math.max(0, l.indexOf(':'))).split(';')[0].toUpperCase().replace(/^ITEM\d+\./, '');
    if (/^BEGIN:VCARD/i.test(l)) c = { nombre: '', apellido: '', email: '', telefono: '', empresa: '', cargo: '', fn: '', telPrio: 9 };
    else if (!c) continue;
    else if (/^END:VCARD/i.test(l)) {
      if (!c.nombre && !c.apellido && c.fn) [c.nombre, c.apellido] = partirNombre(c.fn);
      const { fn: _fn, telPrio: _p, ...contacto } = c;
      out.push(contacto);
      c = null;
    } else if (clave === 'N') {
      const [apellido = '', nombre = ''] = valorVcard(l).split(';');
      c.nombre = nombre.trim();
      c.apellido = apellido.trim();
    } else if (clave === 'FN') c.fn = valorVcard(l);
    else if (clave === 'EMAIL' && !c.email) c.email = valorVcard(l).toLowerCase();
    else if (clave === 'ORG') c.empresa = valorVcard(l).split(';')[0].trim();
    else if (clave === 'TITLE') c.cargo = valorVcard(l);
    else if (clave === 'TEL') {
      const tipo = l.slice(0, l.indexOf(':')).toUpperCase();
      const prio = tipo.includes('WORK') ? 0 : tipo.includes('CELL') ? 1 : 2;
      if (prio < c.telPrio) {
        c.telefono = valorVcard(l).replace(/^tel:/i, '');
        c.telPrio = prio;
      }
    }
  }
  return out;
}

/* ---------------- CSV ---------------- */

export function filasCsv(texto: string): string[][] {
  const t = texto.replace(/^﻿/, '');
  const primera = t.slice(0, t.indexOf('\n') >>> 0);
  const sep = (primera.match(/;/g)?.length ?? 0) > (primera.match(/,/g)?.length ?? 0) ? ';' : ',';
  const filas: string[][] = [];
  let fila: string[] = [];
  let celda = '';
  let comillas = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (comillas) {
      if (ch === '"' && t[i + 1] === '"') (celda += '"'), i++;
      else if (ch === '"') comillas = false;
      else celda += ch;
    } else if (ch === '"') comillas = true;
    else if (ch === sep) fila.push(celda), (celda = '');
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && t[i + 1] === '\n') i++;
      fila.push(celda);
      if (fila.some((x) => x.trim())) filas.push(fila);
      fila = [];
      celda = '';
    } else celda += ch;
  }
  fila.push(celda);
  if (fila.some((x) => x.trim())) filas.push(fila);
  return filas;
}

// Encabezados conocidos de Outlook (ingles y español), Google y hojas hechas a mano.
const COLUMNAS: Record<keyof ContactoLibreta | 'completo', string[]> = {
  nombre: ['first name', 'nombre', 'given name', 'primer nombre'],
  apellido: ['last name', 'apellidos', 'apellido', 'surname', 'family name'],
  completo: ['name', 'full name', 'display name', 'nombre completo', 'nombre para mostrar'],
  email: ['e-mail address', 'email address', 'e-mail 1 - value', 'email', 'e-mail', 'correo', 'correo electronico', 'direccion de correo electronico'],
  telefono: ['business phone', 'mobile phone', 'primary phone', 'phone 1 - value', 'telefono del trabajo', 'telefono movil', 'movil', 'celular', 'telefono', 'phone'],
  empresa: ['company', 'organization 1 - name', 'organizacion', 'organization', 'empresa', 'compania', 'compañia'],
  cargo: ['job title', 'organization 1 - title', 'puesto', 'cargo', 'title'],
};

export const leerCsv = (texto: string): ContactoLibreta[] => deFilas(filasCsv(texto));

/** Contactos desde filas ya leidas (CSV o Excel): la primera fila son los titulos. */
export function deFilas(todas: string[][]): ContactoLibreta[] {
  const [enc, ...filas] = todas;
  if (!enc) return [];
  const cab = enc.map((h) => normalize(h.trim()));
  const col = (k: keyof typeof COLUMNAS) => {
    for (const nombre of COLUMNAS[k]) {
      const i = cab.indexOf(normalize(nombre));
      if (i >= 0) return i;
    }
    return -1;
  };
  const idx = Object.fromEntries(Object.keys(COLUMNAS).map((k) => [k, col(k as keyof typeof COLUMNAS)])) as Record<keyof typeof COLUMNAS, number>;
  const v = (f: string[], k: keyof typeof COLUMNAS) => (idx[k] >= 0 ? (f[idx[k]] ?? '').trim() : '');
  return filas.map((f) => {
    let nombre = v(f, 'nombre');
    let apellido = v(f, 'apellido');
    if (!nombre && !apellido) [nombre, apellido] = partirNombre(v(f, 'completo'));
    return { nombre, apellido, email: v(f, 'email').toLowerCase(), telefono: v(f, 'telefono'), empresa: v(f, 'empresa'), cargo: v(f, 'cargo') };
  });
}

/* ---------------- comun ---------------- */

export function partirNombre(completo: string): [string, string] {
  const p = completo.trim().split(/\s+/).filter(Boolean);
  if (p.length <= 1) return [p[0] ?? '', ''];
  if (p.length === 2) return [p[0], p[1]];
  return [p.slice(0, p.length - 2).join(' '), p.slice(-2).join(' ')];
}

export const leerLibreta = (nombreArchivo: string, texto: string) => (/\.vcf$/i.test(nombreArchivo) || /^\s*BEGIN:VCARD/i.test(texto) ? leerVcard(texto) : leerCsv(texto));

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Separa lo que se puede traer: sin correo ni telefono no sirve; correo repetido (en el archivo o ya en el CRM) se salta.
export function clasificar(contactos: ContactoLibreta[], correosExistentes: Set<string>) {
  const nuevos: ContactoLibreta[] = [];
  let repetidos = 0;
  let incompletos = 0;
  const vistos = new Set<string>();
  for (const c0 of contactos) {
    const c = { ...c0, email: EMAIL.test(c0.email) ? c0.email : '' };
    if (!c.email && !c.telefono.replace(/\D/g, '')) {
      incompletos += 1;
      continue;
    }
    if (!c.nombre && !c.apellido) c.nombre = c.email ? c.email.split('@')[0] : c.telefono;
    if (c.email && (vistos.has(c.email) || correosExistentes.has(c.email))) {
      repetidos += 1;
      continue;
    }
    if (c.email) vistos.add(c.email);
    nuevos.push(c);
  }
  return { nuevos, repetidos, incompletos };
}

// Los CSV de Outlook en Windows suelen venir en ANSI (Windows-1252), no en UTF-8.
export function decodificar(buf: ArrayBuffer) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-1252').decode(buf);
  }
}
